/**
 * RunPage — end-user view of the application, opened in a new tab.
 *
 * Thin route wrapper around EndUserAppView (which is the shared
 * implementation, also used inside Builder Mode's preview pane).
 *
 * Auth: relies on the same httpOnly cookies the rest of the app uses;
 * no Authorization header needed. If the user isn't logged in we send
 * them back to /.
 *
 * TWO WAYS IN. On the platform the application is named by the URL
 * (`/run/{tenant}/{project}`) and the params below carry it. On the
 * application's OWN domain (erp.acme.com) the HOST names it, the app sits at
 * the root, and `identity` carries what the path no longer says — see
 * `config/hostBinding`. Everything after that resolution is identical, which
 * is the point: a domain is an address for this view, not a second one.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useParams, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { useThemeStore } from '@/store/themeStore'
import EndUserAppView from './EndUserAppView'
import UserMemoryGate from '@/components/userMemory/UserMemoryGate'
import ProjectsModal from '@/components/layout/ProjectsModal'
import ProjectAuthModal from './ProjectAuthModal'
import toast from 'react-hot-toast'
import { getSiteToken, setSiteToken, siteRunIdentity } from '@/api/website'
import { runAppAdopt, runAppBootstrap, runAppIdentity, type RunAppInfo } from '@/api/runapp'
import {
  endRunSession, getRunSession, isAdoptSuppressed, onRunSessionChange, startRunSession,
} from '@/api/runSession'
import {
  clearAppEnv, rememberRequestedEnv, requestedEnv, sanitizeEnv, setAppEnv,
} from '@/api/appEnv'

/** Query flag that forces embedded (chromeless) mode on or off. */
const EMBED_PARAM = 'embed'
/** Query flag that picks the theme when embedded (`light` | `dark`). */
const THEME_PARAM = 'theme'
/** Query flag naming a release-management environment (`stage:<n>` — QC on
 *  one fix's staging copy). The frame's, not the screen's: see `api/appEnv`. */
const ENV_PARAM = 'env'

/** Who the application is, when the URL does not say (a bound domain). */
export interface RunIdentity {
  tenant: string
  project: string
  ownerUserId: string
  /** The platform's own address — the only route back to Twasta from here. */
  platformUrl: string
}

export default function RunPage({ identity }: { identity?: RunIdentity }) {
  // On a bound domain the application is the whole site: it lives at the
  // root, the platform's pages are not reachable, and the URL's tenant needs
  // no reconciling because there is none.
  const hostMode = !!identity
  // `/run/:tenant/:project/*` — tenant scopes the URL to the owning org; the
  // splat carries the open object's obj_name so the URL deep-links the current
  // screen (shareable / bookmarkable). In-screen state rides the query string.
  const {
    tenant: tenantParam, project: projectParam, '*': splat,
  } = useParams<{ tenant: string; project: string; '*'?: string }>()
  const tenant = identity?.tenant || (tenantParam ? decodeURIComponent(tenantParam) : '_')
  const project = identity?.project || (projectParam ? decodeURIComponent(projectParam) : '')
  const objName = splat ? decodeURIComponent(splat.split('/')[0] || '') : ''
  const { user, loading: authLoading } = useAuth()
  const { theme, setTheme } = useThemeStore()
  const location = useLocation()
  const navigate = useNavigate()

  // Path prefix every in-app navigation is built on: the `/run/…` route on
  // the platform, and nothing at all on the application's own domain, where
  // `/purchase_order` IS the application's purchase-order screen.
  const runBase = hostMode
    ? ''
    : `/run/${encodeURIComponent(tenant)}/${encodeURIComponent(project)}`
  const runHome = runBase || '/'

  /** Leave for the platform — the one link out of a bound domain. */
  const toPlatform = useCallback((path: string) => {
    const base = (identity?.platformUrl || '').replace(/\/$/, '')
    window.location.href = base ? `${base}${path}` : path
  }, [identity])

  // Reflect the open object in the URL. Changing object resets in-screen
  // state (query), so we navigate to a clean path.
  const onObjectChange = useCallback((obj: string | null, opts?: { replace?: boolean }) => {
    navigate(obj ? `${runBase}/${encodeURIComponent(obj)}` : runHome, opts?.replace ? { replace: true } : undefined)
  }, [runBase, runHome, navigate])

  // Embedded mode — the app is opened inside a host application (Proteus
  // Vision iframes `/run/{tenant}/{project}/{object}`), which brings its own
  // menu tree and header. Drawing ours as well gives the user two of each, so
  // embedded mode renders the object alone.
  //
  // Being framed IS the signal, so a host needs no URL change; `?embed=1` /
  // `?embed=0` forces it either way (useful for testing the embedded layout in
  // a normal tab, and for a host that DOES want our chrome).
  const embedded = useMemo(() => {
    const raw = new URLSearchParams(location.search).get(EMBED_PARAM)
    if (raw !== null) return /^(1|true|yes)$/i.test(raw.trim())
    try {
      return window.self !== window.top
    } catch {
      // Cross-origin ancestor: the access threw, which only happens when there
      // IS one — i.e. we are framed.
      return true
    }
  }, [location.search])

  // In-screen state (e.g. an open transaction record) rides the query string
  // so a deep link restores it. Keyed within the current object's path.
  // `embed` is ours, not the screen's — it must not reach the object as a value.
  const screenState = useMemo(() => {
    const out: Record<string, string> = {}
    new URLSearchParams(location.search).forEach((v, k) => {
      if (k !== EMBED_PARAM && k !== THEME_PARAM && k !== ENV_PARAM) out[k] = v
    })
    return out
  }, [location.search])

  /**
   * Open a DIFFERENT object WITH in-screen state, in one navigation — what a
   * search result needs. `onObjectChange` clears the query and
   * `onScreenStateChange` keeps the current object, so neither can express
   * "open Purchase Orders at record 1042".
   *
   * The host's `embed` / `theme` flags are carried across for the same reason
   * they are in `onScreenStateChange`: they describe the FRAME the app is
   * running in, not the screen, and dropping them mid-session would put our
   * chrome back inside a host that already draws its own.
   */
  const onOpenTarget = useCallback((target: {
    objName: string
    params: Record<string, string>
  }) => {
    const base = `${runBase}/${encodeURIComponent(target.objName)}`
    const params = new URLSearchParams(target.params)
    const current = new URLSearchParams(location.search)
    const embedRaw = current.get(EMBED_PARAM)
    if (embedRaw !== null) params.set(EMBED_PARAM, embedRaw)
    const themeRaw = current.get(THEME_PARAM)
    if (themeRaw !== null) params.set(THEME_PARAM, themeRaw)
    const qs = params.toString()
    navigate(qs ? `${base}?${qs}` : base)
  }, [runBase, navigate, location.search])

  const onScreenStateChange = useCallback((next: Record<string, string> | null) => {
    if (!objName) return
    const base = `${runBase}/${encodeURIComponent(objName)}`
    const params = new URLSearchParams(next || {})
    // An explicit `embed` is the host's instruction, not screen state — carry
    // it across, or the first in-screen change would rewrite the query without
    // it and the chrome would reappear mid-session.
    const embedRaw = new URLSearchParams(location.search).get(EMBED_PARAM)
    if (embedRaw !== null) params.set(EMBED_PARAM, embedRaw)
    const themeRaw = new URLSearchParams(location.search).get(THEME_PARAM)
    if (themeRaw !== null) params.set(THEME_PARAM, themeRaw)
    const qs = params.toString()
    navigate(qs ? `${base}?${qs}` : base, { replace: true })
  }, [runBase, objName, navigate, location.search])

  // ── Which ENVIRONMENT of the application ──────────────────────────────
  //
  // QC's "Open staging" lands here with `?env=stage:<n>`. The view's own
  // navigation drops the query on the next click, so the choice is remembered
  // for this tab (a reload keeps it, a new tab does not) and every request
  // names it through `api/appEnv`. Bound in a LAYOUT effect: those all run
  // before any child's passive effect, so the application's very first
  // request already carries it — a first load from production followed by a
  // staging refresh would be exactly the mix-up the banner exists to prevent.
  const urlEnv = sanitizeEnv(new URLSearchParams(location.search).get(ENV_PARAM))
  const runEnv = urlEnv || (project ? sanitizeEnv(requestedEnv(project)) : null)
  useLayoutEffect(() => {
    if (!project) return undefined
    if (urlEnv) rememberRequestedEnv(project, urlEnv)
    setAppEnv({ project, env: runEnv || undefined, source: 'run' })
    return () => clearAppEnv('run')
  }, [project, urlEnv, runEnv])

  const exitEnv = useCallback(() => {
    rememberRequestedEnv(project, null)
    setAppEnv({ project, source: 'run' })
    const params = new URLSearchParams(location.search)
    params.delete(ENV_PARAM)
    const qs = params.toString()
    // A full reload: everything on screen was read from the staging copy.
    window.location.replace(`${location.pathname}${qs ? `?${qs}` : ''}`)
  }, [project, location.pathname, location.search])

  // Embedded in a host application (Proteus Vision), the host draws light
  // chrome around us and our own top bar — the only place the theme can be
  // toggled — isn't rendered, so a dark preference carried over from the
  // builder tab would sit as a dark panel inside a light host with no way to
  // fix it. Default to light there; `?theme=dark` lets a host ask otherwise.
  // Not persisted: this is the host's context, not the user's choice.
  useEffect(() => {
    if (!embedded) return
    const raw = (new URLSearchParams(location.search).get(THEME_PARAM) || '').trim().toLowerCase()
    setTheme(raw === 'dark' ? 'dark' : 'light', false)
  }, [embedded, location.search, setTheme])

  // Apply theme to body so vars from globals.css resolve correctly.
  useEffect(() => {
    document.body.className = theme === 'light' ? 'light-theme' : ''
  }, [theme])

  // Document title — handy when the user has many Run tabs open.
  useEffect(() => {
    // On its own domain the tab is the application, not a Run view of one.
    if (project) document.title = hostMode ? project : `${project} · Run`
  }, [project, hostMode])

  // ── WHO is running this application ───────────────────────────────────
  //
  // Exactly one answer, held in one place. A Twasta account runs on its
  // platform cookie; a PROJECT USER — invited from the application's own
  // System administration screen, with no Twasta account — runs on the
  // registry session they signed into the application with. Both are possible
  // for the same human, so the choice is made here and never inferred further
  // down: `runAs` is what every header, every identity call and every sign-out
  // in this view follows.
  //
  // 'deciding'      — still working it out (auth loading, or bootstrapping).
  // 'platform'      — acting as the signed-in Twasta account.
  // 'project-user'  — acting as this application's own user.
  // Always starts undecided, even when a session from an earlier mount is
  // still in force: the decision is re-derived below from the platform session
  // and the stored token together, and seeding it from half of that would let
  // a leftover session decide for a Twasta account that is signed in.
  const [runAs, setRunAs] = useState<'deciding' | 'platform' | 'project-user'>('deciding')
  // The public description of the application behind this URL — above all WHO
  // OWNS IT, which the URL does not carry and which every project-user
  // endpoint is keyed by. Fetched only when the platform account is not the
  // answer, so a platform member's Run view makes not one extra request.
  const [appInfo, setAppInfo] = useState<RunAppInfo | null>(null)
  const [bootstrapError, setBootstrapError] = useState<string | null>(null)
  // Set when a signed-in Twasta account turns out to have no access to this
  // application AND could not be let in as itself (see `needsProjectSignIn`).
  const [signInAsProjectUser, setSignInAsProjectUser] = useState(false)

  // The application's owner for the session in force. Held apart from
  // `appInfo` because the invitation-accept link brings its own owner and
  // lands in the application before the public bootstrap has been asked
  // anything.
  const [runOwner, setRunOwner] = useState('')

  /** Act as this application's own user from here on. */
  const actAsProjectUser = useCallback((owner: string, token?: string) => {
    startRunSession({ owner, project, tenant }, token)
    setRunOwner(owner)
    setSignInAsProjectUser(false)
    setRunAs('project-user')
  }, [project, tenant])

  /**
   * This Twasta account cannot open the application. Before asking for another
   * password, ask the SERVER whether it needs one: the platform already binds a
   * project-user account to a platform account by verified email (it is how
   * their functional roles are found — `runtime_rights.registry_user_id`), so
   * where that binding exists the person has already proved who they are and a
   * second password proves nothing. `adopt` opens their own project-user
   * session; the form is for the case where there is genuinely no such account
   * to open — somebody else's application, or an invitation not yet accepted.
   */
  const needsProjectSignIn = useCallback(async () => {
    // Not after a deliberate sign-out. Adopting needs no password, so doing it
    // here would silently put the person back into the session they just left
    // — the form is the whole point of having signed out.
    if (isAdoptSuppressed(project)) { setSignInAsProjectUser(true); return }
    try {
      const r = await runAppAdopt(tenant, project)
      actAsProjectUser(r.data.owner_user_id, r.data.token)
    } catch {
      setSignInAsProjectUser(true)
    }
  }, [tenant, project, actAsProjectUser])

  // Needed while the question is open, whoever is asking: step 1 above reads a
  // stored session keyed by the application's OWNER, which only this answers.
  const needsAppInfo = runAs !== 'platform' || signInAsProjectUser
  useEffect(() => {
    if (!needsAppInfo || !project || appInfo || authLoading) return
    let cancelled = false
    setBootstrapError(null)
    runAppBootstrap(tenant, project)
      .then((r) => { if (!cancelled) setAppInfo(r.data) })
      .catch(() => {
        if (!cancelled) setBootstrapError('It may not exist, or the address may be wrong.')
      })
    return () => { cancelled = true }
  }, [needsAppInfo, tenant, project, appInfo, authLoading])

  // Settle the question, in the order the URL implies. `/run/...` is the
  // application, so the first question is "am I a user OF this application",
  // and only then "am I a Twasta account that was given it". Asked in the
  // other order, somebody who is both would run as the platform account and
  // never see the roles the application's own administrator gave them.
  //
  //   1. a session for this application already in hand — use it;
  //   2. signed in to Twasta — ask the server whether that account IS a user
  //      of this application (`adopt`, no password: the platform already binds
  //      the two by verified email). Yes ⇒ run as that application user;
  //   3. no ⇒ run as the Twasta account, which then stands or falls on whether
  //      the application was shared with it — allowed if it was, refused if
  //      it was not;
  //   4. not signed in to anything ⇒ the application's own sign-in (below).
  //
  // Except for the application's own BUILDERS (its owner and the people it was
  // shared with to build): their Twasta account already sees every screen, so
  // an application account under the same email — typically `registered-user`
  // from signing up on its site — would only hide the menu from them. They run
  // as themselves; the stored application session is left alone (it is shared
  // with the published pages), only not acted on here.
  const adoptAsked = useRef(false)
  useEffect(() => {
    if (authLoading || runAs !== 'deciding' || !appInfo) return
    const owner = appInfo.owner_user_id
    if (!user) {
      // anonymous: a stored session, else the sign-in gate below handles it
      if (getSiteToken(owner, project)) actAsProjectUser(owner)
      return
    }
    if (adoptAsked.current) return
    adoptAsked.current = true
    let cancelled = false
    const asApplicationUser = () => {
      if (getSiteToken(owner, project)) { actAsProjectUser(owner); return }
      if (isAdoptSuppressed(project)) { setRunAs('platform'); return }
      runAppAdopt(tenant, project)
        .then((r) => {
          if (!cancelled) actAsProjectUser(r.data.owner_user_id, r.data.token)
        })
        .catch(() => {
          // Not a user of this application. That is not a refusal in itself —
          // it just means the Twasta account answers for itself from here.
          if (!cancelled) setRunAs('platform')
        })
    }
    siteRunIdentity(owner, project)
      .then((r) => {
        if (cancelled) return
        if (r.data?.authors_app) setRunAs('platform')
        else asApplicationUser()
      })
      // Not shared with this Twasta account (or the check failed): the
      // application account is the only way in, as before.
      .catch(() => { if (!cancelled) asApplicationUser() })
    return () => { cancelled = true }
  }, [authLoading, runAs, user, appInfo, project, tenant, actAsProjectUser])

  // Keep the module-level session in step with the decision — it IS the
  // decision, expressed where the request interceptor can read it. Written as
  // one effect over `runAs` rather than as a mount/unmount pair so it is
  // idempotent: React re-runs effects (StrictMode does it on every mount), and
  // a teardown that only ran once would leave the view believing it was acting
  // as a project user while sending no headers at all.
  //
  // The token itself is never dropped here — it is the application's own
  // session, shared with its published pages. Only the decision to act as it
  // is, which is what keeps the two identities from ever both being in force.
  useEffect(() => {
    if (runAs !== 'project-user' || !runOwner) {
      if (getRunSession()) endRunSession(false)
      return undefined
    }
    startRunSession({ owner: runOwner, project, tenant })
    return () => { if (getRunSession()) endRunSession(false) }
  }, [runAs, runOwner, project, tenant])

  // The SERVER can end the session under us — it expired, an administrator
  // reset the person's password or two-factor sign-in, or they signed out on
  // another device. The API client then drops the token (`endRunSession`, on a
  // 401 marked X-Twasta-Session); without this the view would go on believing
  // it acts as a project user while sending no identity at all, and hang. Go
  // back to deciding instead: the application's own sign-in for an anonymous
  // visitor, or `adopt` again for a signed-in Twasta account. A teardown that
  // keeps the token (`endRunSession(false)`) is not an ending and is ignored.
  useEffect(() => {
    if (runAs !== 'project-user' || !runOwner) return undefined
    return onRunSessionChange((s) => {
      if (s || getSiteToken(runOwner, project)) return
      adoptAsked.current = false
      setRunOwner('')
      setRunAs('deciding')
      toast.error('Your session for this application has ended — please sign in again.')
    })
  }, [runAs, runOwner, project])

  const projectUserReady = runAs === 'project-user'

  // A stored application session can outlive its usefulness (expired, role
  // removed, or never meant for this Twasta account — e.g. the application's
  // own builder). Run on it and every request answers 403 "no permission".
  // So when a signed-in Twasta account is about to act as a project user,
  // confirm the session still holds; if the server refuses it, forget that
  // token and let the Twasta account answer for itself.
  const sessionChecked = useRef('')
  useEffect(() => {
    if (runAs !== 'project-user' || !runOwner || !user) return undefined
    const key = `${runOwner}/${project}`
    if (sessionChecked.current === key) return undefined
    sessionChecked.current = key
    let cancelled = false
    runAppIdentity(project).catch((e: { response?: { status?: number } }) => {
      const status = e?.response?.status
      if (cancelled || (status !== 401 && status !== 403)) return
      setSiteToken(runOwner, project, null)
      endRunSession(false)
      setRunOwner('')
      setRunAs('platform')
    })
    return () => { cancelled = true }
  }, [runAs, runOwner, user, project])

  // Project-user (Type-2) onboarding from an emailed link: accept an invite
  // (set password) or reset a password. These work WITHOUT a Twasta login, so
  // they run BEFORE the Twasta-auth redirect. The owner storage id rides the
  // link so the endpoints resolve without platform auth.
  const _q = new URLSearchParams(location.search)
  const puAccept = _q.get('pu_accept')
  const puReset = _q.get('pu_reset')
  const puOwner = _q.get('owner') || ''
  if ((puAccept || puReset) && puOwner) {
    return (
      <div style={{ height: '100vh', width: '100vw', background: 'var(--color-bg)' }}>
        <ProjectAuthModal
          userId={puOwner}
          project={project}
          initialMode={puAccept ? 'accept' : 'reset'}
          token={(puAccept || puReset) as string}
          onAuthed={(tok) => {
            // Straight into the application. Accepting an invitation is the
            // moment this person's account becomes usable, and sending them to
            // a sign-in form to type the password they have just chosen is the
            // dead end this whole path exists to remove. Acting as the new
            // account also settles it for somebody who happens to hold a
            // Twasta login too: they followed THIS invitation.
            actAsProjectUser(puOwner, tok)
            navigate(runHome, { replace: true })
          }}
          onClose={() => navigate(runHome, { replace: true })}
        />
      </div>
    )
  }

  // ── Who is running this application? ──────────────────────────────────
  //
  // A Twasta account signs into the platform; a PROJECT USER signs into the
  // application itself. Both open the same URL, so the answer is settled here
  // once, before the application is asked for anything, and the two are never
  // in force at the same time (see `api/runSession`).
  if (authLoading || (runAs === 'deciding' && !appInfo && !bootstrapError)) {
    return <RunPageSpinner />
  }

  if (runAs !== 'project-user' && !user) {
    // No Twasta session. This used to redirect to the platform login, which is
    // a dead end for the people this view is FOR: someone invited from the
    // application's own System administration screen has no Twasta account to
    // log into, and never will. So the application's own sign-in is offered
    // instead, with the platform login still one click away for a teammate who
    // simply has not signed in yet.
    // Where a Twasta sign-in happens. On the platform it is a route; on a
    // bound domain the platform is a different origin, so the whole Run URL
    // has to be rebuilt there — the domain's own path means nothing to it.
    const platformSignIn = () => {
      if (hostMode) {
        const dest = `/run/${encodeURIComponent(tenant)}/${encodeURIComponent(project)}`
        toPlatform(`/?next=${encodeURIComponent(dest)}`)
        return
      }
      navigate(`/?next=${encodeURIComponent(location.pathname + location.search)}`)
    }
    if (bootstrapError || !appInfo) {
      return (
        <RunPageNotice
          title={`"${project}" isn't available`}
          body={bootstrapError || 'This application could not be found.'}
          actionLabel={hostMode && !identity?.platformUrl ? undefined : 'Sign in to Twasta'}
          onAction={hostMode && !identity?.platformUrl ? undefined : platformSignIn}
        />
      )
    }
    if (!appInfo.project_users) {
      // The application does not take project-user sign-in at all, so a Twasta
      // account is the only way in. On the platform that is a redirect; on a
      // bound domain it is a journey to another origin, which is a thing to
      // OFFER rather than to perform behind the visitor's back.
      if (hostMode) {
        return (
          <RunPageNotice
            title={appInfo.title || project}
            body="This application is opened with a Twasta account."
            actionLabel={identity?.platformUrl ? 'Sign in to Twasta' : undefined}
            onAction={identity?.platformUrl ? platformSignIn : undefined}
          />
        )
      }
      const dest = `${location.pathname}${location.search}`
      return <Navigate to={`/?next=${encodeURIComponent(dest)}`} replace />
    }
    return (
      <div style={{ height: '100vh', width: '100vw', background: 'var(--color-bg)' }}>
        <ProjectAuthModal
          userId={appInfo.owner_user_id}
          project={project}
          onAuthed={(tok) => actAsProjectUser(appInfo.owner_user_id, tok)}
          onClose={() => {
            // Dismissing the sign-in on a bound domain leaves nowhere to go —
            // this host IS the application — so it stays put rather than
            // dropping the visitor on a blank page.
            if (hostMode) return
            const dest = `${location.pathname}${location.search}`
            navigate(`/?next=${encodeURIComponent(dest)}`)
          }}
        />
      </div>
    )
  }

  // Keep the URL's tenant honest: data is always resolved in the caller's
  // tenant (tenants are isolated), so normalise a stale/foreign tenant segment
  // to the session tenant rather than show a misleading URL.
  if (!hostMode && user?.tenant_id && tenant !== '_' && tenant !== user.tenant_id) {
    const rest = splat ? `/${splat}` : ''
    return (
      <Navigate
        to={`/run/${encodeURIComponent(user.tenant_id)}/${encodeURIComponent(project)}${rest}${location.search}`}
        replace
      />
    )
  }

  return (
    <div
      style={{
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        background: 'var(--color-bg)',
      }}
    >
      <EndUserAppView
        project={project}
        variant="run"
        // As a project user this is the APPLICATION's owner — the id its site
        // endpoints and embedded pages are keyed by. As a Twasta account it
        // stays the caller's own id, exactly as before.
        ownerUserId={projectUserReady ? runOwner : user?.user_id}
        platformUser={projectUserReady ? null : user}
        projectUser={projectUserReady}
        singleApp={hostMode}
        onNeedsProjectSignIn={
          appInfo?.project_users === false ? undefined : needsProjectSignIn
        }
        initialObjName={objName}
        onObjectChange={onObjectChange}
        embedded={embedded}
        screenState={screenState}
        onScreenStateChange={onScreenStateChange}
        onOpenTarget={onOpenTarget}
        runEnv={runEnv}
        onExitEnv={runEnv ? exitEnv : undefined}
      />
      {/* A Twasta account signed in but refused this application may still
          hold a project-user account for it — the account the application's
          own administrator gave them. Offer that sign-in rather than leaving
          them at a dead end. */}
      {signInAsProjectUser && appInfo?.project_users && !projectUserReady && (
        <ProjectAuthModal
          userId={appInfo.owner_user_id}
          project={project}
          onAuthed={(tok) => actAsProjectUser(appInfo.owner_user_id, tok)}
          onClose={() => setSignInAsProjectUser(false)}
        />
      )}
      {/* Ask the signed-in person for any pending prompt-variable values —
          including the application's security context (e.g. the financial
          entity their rights follow). A project user is asked too: the
          /api/user-memory endpoints serve both kinds of caller
          (`get_run_caller`), keyed by the registry account's email, which is
          what `runtime_rights.resolve_context` reads back. */}
      {(runAs === 'platform' || projectUserReady) && (
        <UserMemoryGate projectId={project} surface="internal" />
      )}
      {/* App switcher — opened from the "Apps" button in the run top bar. Lets
          the user jump to another application (Run / Workbench / Builder per
          their rights) without needing the workbench chrome. A project user
          has exactly one application, so there is nothing to switch to. */}
      {!projectUserReady && !hostMode && <ProjectsModal />}
    </div>
  )
}

/** Full-screen spinner while the Run view works out who is asking. */
function RunPageSpinner() {
  return (
    <div style={{
      height: '100vh', width: '100vw', background: 'var(--color-bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    }}>
      <Loader2 size={22} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
      <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>Loading…</span>
    </div>
  )
}

/** Full-screen message with one way forward. */
function RunPageNotice({ title, body, actionLabel, onAction }: {
  title: string
  body: string
  actionLabel?: string
  onAction?: () => void
}) {
  return (
    <div style={{
      height: '100vh', width: '100vw', background: 'var(--color-bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{ maxWidth: 380, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)' }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>{body}</div>
        {actionLabel && onAction && (
          <button
            onClick={onAction}
            style={{
              marginTop: 6, alignSelf: 'center', padding: '6px 14px', fontSize: 13,
              borderRadius: 6, border: '1px solid var(--color-border)',
              background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer',
            }}
          >
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Compatibility for a tenant-less `/run/:project` link: redirect to the
 * tenant-scoped `/run/:tenant/:project` using the signed-in user's tenant.
 */
export function RunRedirect() {
  const { project: projectParam } = useParams<{ project: string }>()
  const project = projectParam || ''
  const { user, loading } = useAuth()
  const location = useLocation()

  if (!loading && !user) {
    const dest = `${location.pathname}${location.search}`
    return <Navigate to={`/?next=${encodeURIComponent(dest)}`} replace />
  }
  if (loading) return null
  const tenant = user?.tenant_id || '_'
  return (
    <Navigate
      to={`/run/${encodeURIComponent(tenant)}/${encodeURIComponent(project)}${location.search}`}
      replace
    />
  )
}
